import { useEffect, useRef, useState } from 'react'
import type { FC, PropsWithChildren } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { LeftOutlined } from '@ant-design/icons'

import { rqKeys } from '@/react-query-keys'
import { E_LsKeys } from '@/helpers/ls-keys'
import {
  apiRequest,
  AUTH_LOGOUT_BROADCAST_EVENT,
  clearAuthTokens,
  clearAuthSessionCookies,
  isLogoutGuardActive,
  isVisitorUnauthorizedError,
  setLogoutGuard,
  setNewApiToken,
} from '@/helpers/request'
import { getShareholderStatusText, isCordovaApp } from '@/helpers'
import { clearIDBPersistedQueryClient } from '@/helpers/query-persister'
import { routes } from '@/router/routes'
import { useAppStore } from '@/store'
import { Skeleton } from '@/components'
import { isOnline } from '@/b-components/pwa/helpers'
import { getMs } from '@/helpers/time-utils'
import {
  TOPUP_BALANCE_CHANGED_EVENT,
  clearTopupBalancePendingSession,
  hasTopupBalancePendingCookie,
  TOPUP_BALANCE_PENDING_UPDATED_EVENT,
  readTopupBalancePendingSession,
} from '@/helpers/payment/topup-balance-pending-session'
import type {
  T_TopupBalancePendingBaseline,
  T_TopupBalancePendingSession,
} from '@/helpers/payment/topup-balance-pending-session'
import type { T_UniRes } from '@/types'
import type {
  T_UserDataRes,
  T_StructureListRes,
  T_UserBalanceRes,
  T_TransactionsListRes,
} from '@/types/responses'


const TOPUP_RETURN_PATH_RE = /^\/office\/topup\/[^/?#]+\/?$/
const TOPUP_BALANCE_POLL_ABSOLUTE_POINTS_MS = [ 3000, 5000, 8000, 10000 ] as const
const TOPUP_BALANCE_POLL_REPEAT_DELAY_MS = getMs('5s')
const TOPUP_BALANCE_POLL_REPEAT_START_MS = getMs('15s')
const TOPUP_BALANCE_POLL_MAX_DURATION_MS = getMs('2m')
const TOPUP_LAST_PATH_BEFORE_UNLOAD_KEY = 'ERA_TOPUP_LAST_PATH_BEFORE_UNLOAD'

const isReloadNavigation = () => {
  if (typeof window === 'undefined') return false

  try {
    const navEntries = window.performance.getEntriesByType('navigation')
    const navEntry = navEntries[0] as PerformanceNavigationTiming | undefined
    if (navEntry?.type === 'reload') {
      return true
    }

    const legacyNavigation = (window.performance as unknown as {
      navigation?: { type?: number, TYPE_RELOAD?: number }
    }).navigation
    if (
      typeof legacyNavigation?.type === 'number'
      && typeof legacyNavigation?.TYPE_RELOAD === 'number'
      && legacyNavigation.type === legacyNavigation.TYPE_RELOAD
    ) {
      return true
    }
  } catch {
    return false
  }

  return false
}

const readLastPathBeforeUnload = () => {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(TOPUP_LAST_PATH_BEFORE_UNLOAD_KEY)
  } catch {
    return null
  }
}

const writeLastPathBeforeUnload = (pathname: string) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(TOPUP_LAST_PATH_BEFORE_UNLOAD_KEY, pathname)
  } catch {
    // ignore
  }
}

const clearLastPathBeforeUnload = () => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(TOPUP_LAST_PATH_BEFORE_UNLOAD_KEY)
  } catch {
    // ignore
  }
}

const shouldCancelTopupOnThisReload = () => {
  if (typeof window === 'undefined') return false
  if (!isReloadNavigation()) return false

  const lastPathBeforeUnload = readLastPathBeforeUnload()
  clearLastPathBeforeUnload()

  return !!lastPathBeforeUnload && lastPathBeforeUnload === window.location.pathname
}

const buildRuntimeTopupSession = (): T_TopupBalancePendingSession => ({
  startedAt: Date.now(),
  requestId: null,
  sessionKey: `runtime_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
})

const getBalanceSignature = (
  data: (T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }) | undefined
) => {
  const list = data?.data?.list
  if (!Array.isArray(list) || list.length === 0) return null

  return list
    .map((item) => {
      const amountValue = Number(item?.amount?.value) || 0
      const blockedValue = Number(item?.blocked?.value) || 0
      return `${item.id}:${item.currency}:${amountValue}:${blockedValue}`
    })
    .sort()
    .join('|')
}

const getBalanceAccountSnapshot = (
  data: (T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }) | undefined,
  baseline: T_TopupBalancePendingBaseline | null | undefined,
) => {
  if (!baseline) return null

  const list = data?.data?.list
  if (!Array.isArray(list) || list.length === 0) return null

  const balanceAccount = list.find((item) => Number(item?.id) === baseline.accountId)
  if (!balanceAccount) return null

  return {
    accountId: baseline.accountId,
    currencyId: Number(balanceAccount.currency) || null,
    amountValue: Number(balanceAccount.amount?.value) || 0,
    blockedValue: Number(balanceAccount.blocked?.value) || 0,
  }
}

const getTransactionsSignature = (
  data: T_UniRes<T_TransactionsListRes> | undefined
) => {
  const list = data?.data?.list
  if (!Array.isArray(list) || list.length === 0) return null

  return list
    .slice(0, 20)
    .map((item) => {
      const id = Number(item?.id) || 0
      const time = Number(item?.time?.value) || 0
      return `${id}:${time}`
    })
    .join('|')
}

type T_TopupBalancePollingLoaderProps = {
  onClose: () => void
}

const TopupBalancePollingLoader: FC<T_TopupBalancePollingLoaderProps> = ({ onClose }) => (
  <div style={{
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#efefef',
  }}>
    <div style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      justifyContent: 'center',
      padding: '0 8px',
      zIndex: 0,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 520,
        height: '100%',
        background: '#ffffff',
      }} />
    </div>

    <div style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      maxWidth: 520,
      zIndex: 1,
      background: 'transparent',
    }}>
      <button
        type='button'
        onClick={onClose}
        style={{
          marginTop: 'calc(16px + env(safe-area-inset-top, 0px))',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          padding: '0 16px',
          border: 'none',
          background: 'transparent',
          color: '#151515',
          fontSize: 20,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        <LeftOutlined
          style={{
            fontSize: 18,
            color: '#79b640',
            position: 'absolute',
            left: 20,
          }}
        />
        <span>Пополнение баланса</span>
      </button>

      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: '100%',
          maxWidth: 380,
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            fontSize: 24,
            color: '#121212',
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.25,
          }}>
            Деньги зачислятся в течение двух минут
          </div>
        </div>
      </div>

      <div style={{
        padding: '0 16px calc(16px + env(safe-area-inset-bottom, 0px))',
      }}>
        <button
          type='button'
          onClick={onClose}
          style={{
            width: '100%',
            height: 56,
            borderRadius: 10,
            border: 'none',
            background: '#e9e9eb',
            color: '#151515',
            fontSize: 18,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Закрыть
        </button>
      </div>
    </div>
  </div>
)


export const CheckAuth: FC<PropsWithChildren> = ({
  children,
}) => {
  const isAuthorized = useAppStore((s) => s.isAuthorized)
  const setToAuthorized = useAppStore((s) => s.setToAuthorized)
  const setToUnauthorized = useAppStore((s) => s.setToUnauthorized)
  const isAuthCheckedOnLoad = useAppStore((s) => s.isAuthCheckedOnLoad)
  const setToAuthCheckedOnLoad = useAppStore((s) => s.setToAuthCheckedOnLoad)
  const setUserData = useAppStore((s) => s.setUserData)
  const setHasStructureMembers = useAppStore((s) => s.setHasStructureMembers)

  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const topupBalancePollSessionRef = useRef(0)
  const topupPendingFallbackRef = useRef<T_TopupBalancePendingSession | null>(null)
  const shouldCancelTopupOnReloadRef = useRef(shouldCancelTopupOnThisReload())
  const isReloadCancellationAppliedRef = useRef(false)
  const [ isTopupBalancePolling, setIsTopupBalancePolling ] = useState(false)
  const [ activeTopupSessionKey, setActiveTopupSessionKey ] = useState<string | null>(null)
  const [ dismissedTopupSessionKey, setDismissedTopupSessionKey ] = useState<string | null>(null)
  const [ topupPendingRevision, setTopupPendingRevision ] = useState(0)
  const [ lastCordovaResumeAt, setLastCordovaResumeAt ] = useState(0)

  const {
    data: clientInfoRes,
    isPending: isClientInfoPending,
    isError: isClientInfoError,
    error: clientInfoError,
  } = useQuery<T_UniRes<T_UserDataRes>>({
    queryKey: [ rqKeys.userData ],
    queryFn: () => apiRequest({ method: 'client/visitor/info' }),

    // При первом открытии приложения (неавторизованный пользователь) бэкенд возвращает `visitor-unauthorized`.
    // Здесь нельзя допускать циклы retry/refetch.
    // Этот запрос должен выполниться один раз, а дальше обновляться только явно
    // (после логина / reauthorize / keepalive).
    retry: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  })

  // Не вызываем `client/token/generate` при старте приложения, пока пользователь не авторизован.
  // Токен для нового API будет получен лениво (при первом вызове apiRequestNew) после авторизации.
  useEffect(() => {
    const hasRefreshToken = !!localStorage.getItem(E_LsKeys.EraRefreshToken)

    if (!isOnline()) {
      setToAuthCheckedOnLoad()
      // Если есть refresh-токен, в офлайне считаем пользователя авторизованным
      // и показываем кэшированные данные. Сессия восстановится после выхода в онлайн.
      if (hasRefreshToken) setToAuthorized()
    }
  }, [])

  // После загрузки данных пользователя, заполняем store данными профиля
  // и переводим приложение в статус 'Авторизован'
  useEffect(() => {
    if (isClientInfoPending) return

    setToAuthCheckedOnLoad()

    if (clientInfoRes?.code === 'ok') {
      // Do not restore authorized state while logout guard is active.
      if (isLogoutGuardActive()) {
        setToUnauthorized()
        navigate(routes.auth, { replace: true })
        return
      }

      const {
        id,
        login,
        lastname,
        firstname,
        patronymic,
        status,
        mobile,
        email,
        token,
      } = clientInfoRes.data

      const currentUserId = useAppStore.getState().userData.id
      const isUserSwitched = !!currentUserId && currentUserId !== id

      // Если в другом приложении/вкладке произошла смена аккаунта,
      // делаем полный reload приложения, чтобы не смешивать состояния.
      if (isUserSwitched) {
        window.location.reload()
        return
      }

      setUserData({
        id,
        login,
        fullname: lastname || firstname ? `${lastname} ${firstname}` : 'Безымянный',
        lastname: lastname || '',
        firstname: firstname || '',
        patronymic: patronymic || '',
        mobile,
        email,
        shareholderStatusId: status.status,
        shareholderStatus: getShareholderStatusText(status.status),
        sourceCompanyId: status.source_company_id,
      })

      // Запоминаем пришедший рефреш-токен.
      // При каждом запросе данных профиля пользователя токен будет новый.
      localStorage.setItem(E_LsKeys.EraRefreshToken, token)

      setToAuthorized()

      // Проверяем наличие людей в структуре
      ;(async () => {
        try {
          const structureRes = await apiRequest<T_UniRes<T_StructureListRes>>({
            method: 'client/visitor/structure/list',
            param: { id },
          })

          if (structureRes.code === 'ok' && structureRes.data?.list) {
            setHasStructureMembers(structureRes.data.list.length > 0)
          } else {
            setHasStructureMembers(false)
          }
        } catch (error) {
          console.error('Failed to check structure members:', error)
          setHasStructureMembers(false)
        }
      })()
    }
  }, [ clientInfoRes, isClientInfoPending, isClientInfoError, clientInfoError ])

  // разлогиниваем только при явном visitor-unauthorized из /client/visitor/info.
  // Сетевые ошибки/таймауты не должны выбрасывать пользователя.
  useEffect(() => {
    if (!isClientInfoError) return
    if (!isOnline()) return
    if (!isVisitorUnauthorizedError(clientInfoError)) return

    setToUnauthorized()
    navigate(routes.auth, { replace: true })
  }, [ isClientInfoError, clientInfoError, navigate, setToUnauthorized ])

  // Быстрая синхронизация после изменений авторизации в других контекстах:
  // - когда вкладка/окно снова становится активным
  // - когда в другой вкладке/контексте меняется ключ refresh-токена
  // Это предотвращает долгие периоды с устаревшими данными профиля.
  useEffect(() => {
    if (!isAuthorized) return

    let inFlight = false
    let isForceLogoutInFlight = false
    let tokenRefetchTimeoutId: number | null = null
    const TOKEN_STORAGE_REFETCH_DEBOUNCE_MS = getMs('15s')

    const refetchUserData = () => {
      if (inFlight) return
      if (!isOnline()) return

      inFlight = true
      void (async () => {
        try {
          await queryClient.invalidateQueries({
            queryKey: [ rqKeys.userData ],
            exact: true,
            refetchType: 'none',
          })
          await queryClient.refetchQueries({
            queryKey: [ rqKeys.userData ],
            exact: true,
            type: 'active',
          })
        } finally {
          inFlight = false
        }
      })()
    }

    const applyForcedLogout = () => {
      if (isForceLogoutInFlight) return
      isForceLogoutInFlight = true

      setLogoutGuard()
      void queryClient.cancelQueries()
      setToUnauthorized()
      clearAuthSessionCookies()
      clearAuthTokens()
      queryClient.clear()

      void clearIDBPersistedQueryClient().catch((e) => {
        console.warn('[auth-sync] failed to clear persisted cache', e)
      })

      navigate(routes.auth, { replace: true })
    }

    const onFocus = () => {
      refetchUserData()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetchUserData()
      }
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key === E_LsKeys.EraLogoutSync && !!event.newValue) {
        applyForcedLogout()
        return
      }

      if (event.key === E_LsKeys.EraRefreshToken) {
        if (!event.newValue || event.newValue === event.oldValue) return
        if (document.visibilityState !== 'visible') return
        if (typeof document.hasFocus === 'function' && !document.hasFocus()) return

        if (tokenRefetchTimeoutId !== null) {
          window.clearTimeout(tokenRefetchTimeoutId)
        }

        tokenRefetchTimeoutId = window.setTimeout(() => {
          tokenRefetchTimeoutId = null
          refetchUserData()
        }, TOKEN_STORAGE_REFETCH_DEBOUNCE_MS)
      }
    }
    const onBroadcastLogout = () => {
      applyForcedLogout()
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('storage', onStorage)
    window.addEventListener(AUTH_LOGOUT_BROADCAST_EVENT, onBroadcastLogout)

    return () => {
      if (tokenRefetchTimeoutId !== null) {
        window.clearTimeout(tokenRefetchTimeoutId)
      }
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(AUTH_LOGOUT_BROADCAST_EVENT, onBroadcastLogout)
    }
  }, [ isAuthorized, navigate, queryClient, setToUnauthorized ])

  // Поддерживаем visitor-токен старого API актуальным:
  // - пока пользователь авторизован, периодически делаем refetch client/visitor/info
  // - целевая частота:
  //   - минимум раз в 30 минут
  //   - после >3 часов неактивности: раз в час
  useEffect(() => {
    if (!isAuthorized) return

    let lastActivityAt = Date.now()

    const markActivity = () => {
      lastActivityAt = Date.now()
    }

    // Сигналы активности (best effort): работает в web/PWA, но iOS в фоне может замедлять таймеры.
    const onPointerDown: EventListener = () => markActivity()
    const onKeyDown: EventListener = () => markActivity()
    const onFocus: EventListener = () => markActivity()

    window.addEventListener('pointerdown', onPointerDown, { passive: true })
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('focus', onFocus)

    const onVisibility = () => {
      if (document.visibilityState === 'visible') markActivity()
    }

    document.addEventListener('visibilitychange', onVisibility)

    let timeoutId: number | null = null

    const getIntervalMs = () => {
      const idleMs = Date.now() - lastActivityAt
      return idleMs > getMs('3h') ? getMs('1h') : getMs('30m')
    }

    const schedule = (delayMs: number) => {
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      timeoutId = window.setTimeout(() => {
        void run()
      }, delayMs)
    }

    const run = async () => {
      try {
        // Если офлайн, не спамим ошибками — просто планируем следующую проверку.
        if (isOnline()) {
          // Делаем refetch userData, который также обновляет и сохраняет EraRefreshToken.
          await queryClient.refetchQueries({ queryKey: [ rqKeys.userData ] })
        }
      } catch (e) {
        console.warn('[keepalive] client/visitor/info failed', e)
      } finally {
        schedule(getIntervalMs())
      }
    }

    // Первая keepalive-проверка запускается через рассчитанный интервал (без лишнего запроса сразу при монтировании).
    schedule(getIntervalMs())

    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }, [ isAuthorized, queryClient ])

  // Обновляем токен для NEW API (client/token/generate) каждые 15 минут, когда есть интернет.
  // Этот токен используется в apiRequestNew через localStorage(EraToken).
  useEffect(() => {
    if (!isAuthorized) return

    let timeoutId: number | null = null
    let stop = false
    let lastRefreshAt = 0

    const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

    const refreshNow = async () => {
      if (stop) return
      if (!isOnline()) return

      // Защита от случайных частых циклов запросов.
      const now = Date.now()
      if (lastRefreshAt && now - lastRefreshAt < getMs('1m')) return
      lastRefreshAt = now

      const maxAttempts = 3
      const delaysMs = [ 0, getMs('1500ms'), getMs('4s') ]

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (stop) return
        if (!isOnline()) return

        const delay = delaysMs[attempt - 1] ?? getMs('4s')
        if (delay > 0) await sleep(delay)
        if (stop) return

        try {
          const res = await apiRequest<{ data?: { token?: string } }>({
            method: 'client/token/generate',
          })
          if (stop) return

          const token = res?.data?.token
          if (token) {
            setNewApiToken(token)
            return
          }
        } catch (e) {
          // Игнорируем и повторяем с ограничением попыток; не бросаем исключение, чтобы не ломать UI.
          console.warn('[token] client/token/generate failed', e)
        }
      }
    }

    const schedule = (delayMs: number) => {
      if (stop) return
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      timeoutId = window.setTimeout(() => {
        if (stop) return
        void run()
      }, delayMs)
    }

    const run = async () => {
      if (stop) return
      try {
        await refreshNow()
      } finally {
        if (stop) return
        schedule(getMs('15m'))
      }
    }

    // Делаем первое обновление вскоре после авторизации (best effort).
    schedule(getMs('2s'))

    const onOnline = () => {
      // Если офлайн длился долго, вскоре после восстановления сети обновляем токен.
      schedule(getMs('2s'))
    }

    window.addEventListener('online', onOnline)

    return () => {
      stop = true
      window.removeEventListener('online', onOnline)
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }
  }, [ isAuthorized ])

  // Обновление авторизации
  const { mutateAsync: refreshAuthorization } = useMutation({
    mutationFn: () => apiRequest<T_UniRes<{ id: number }>>({
      method: 'client/visitor/authorize_token',
      param: {
        token: localStorage.getItem(E_LsKeys.EraRefreshToken),
      },
    }),
  })

  // Переходы на/с логина зависят от статуса авторизации
  useEffect(() => {
    (async () => {
      if (isAuthorized && location.pathname.startsWith(routes.auth)) {
        navigate(routes.home, { replace: true })
      }

      if (isAuthCheckedOnLoad && !isAuthorized) {
        if (localStorage.getItem(E_LsKeys.EraRefreshToken)) {
          if (!isOnline()) return

          try {
            const res = await refreshAuthorization()

            if (res?.code === 'ok') {
              queryClient.invalidateQueries({ queryKey: [ rqKeys.userData ] })
            } else {
              navigate(routes.auth)
            }
          } catch (e) {
            console.error(e)
          }
        } else {
          navigate(routes.auth)
        }
      }
    })()
  }, [ isAuthCheckedOnLoad ])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const persistPathBeforeUnload = () => {
      writeLastPathBeforeUnload(window.location.pathname)
    }

    window.addEventListener('pagehide', persistPathBeforeUnload)
    return () => {
      window.removeEventListener('pagehide', persistPathBeforeUnload)
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!isCordovaApp) return

    const handlePendingSessionRevision = () => {
      setTopupPendingRevision((revision) => revision + 1)
    }

    window.addEventListener(TOPUP_BALANCE_PENDING_UPDATED_EVENT, handlePendingSessionRevision)
    return () => {
      window.removeEventListener(TOPUP_BALANCE_PENDING_UPDATED_EVENT, handlePendingSessionRevision)
    }
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined') return
    if (!isCordovaApp) return

    const handleCordovaResume = () => {
      setLastCordovaResumeAt(Date.now())
    }

    document.addEventListener('resume', handleCordovaResume, false)
    return () => {
      document.removeEventListener('resume', handleCordovaResume, false)
    }
  }, [])

  useEffect(() => {
    if (!isAuthorized) {
      setIsTopupBalancePolling(false)
      setActiveTopupSessionKey(null)
      setDismissedTopupSessionKey(null)
      topupPendingFallbackRef.current = null
      return
    }

    if (shouldCancelTopupOnReloadRef.current && !isReloadCancellationAppliedRef.current) {
      isReloadCancellationAppliedRef.current = true
      clearTopupBalancePendingSession()
      setIsTopupBalancePolling(false)
      setActiveTopupSessionKey(null)
      setDismissedTopupSessionKey(null)
      topupPendingFallbackRef.current = null
    }

    const pendingTopupSessionFromStorage = readTopupBalancePendingSession()
    const hasPendingCookieMarker = hasTopupBalancePendingCookie()
    const isTopupReturnPath = TOPUP_RETURN_PATH_RE.test(location.pathname)
    const hasCordovaReturnMarker = (
      new URLSearchParams(location.search).get('from_cordova') === '1'
    )
    const isTopupReturnContextByPath = isTopupReturnPath || hasCordovaReturnMarker

    if (pendingTopupSessionFromStorage) {
      topupPendingFallbackRef.current = pendingTopupSessionFromStorage
    } else if (hasPendingCookieMarker && !topupPendingFallbackRef.current) {
      // Storage can be unavailable in some environments; keep runtime fallback so polling survives route changes.
      topupPendingFallbackRef.current = buildRuntimeTopupSession()
    }

    const pendingTopupSession = pendingTopupSessionFromStorage || topupPendingFallbackRef.current

    if (!pendingTopupSession) {
      setIsTopupBalancePolling(false)
      setActiveTopupSessionKey(null)
      if (isTopupReturnPath) {
        navigate(routes.home, { replace: true })
      }
      return
    }

    const hasCordovaResumeAfterPendingStart = (
      isCordovaApp
      && lastCordovaResumeAt > 0
      && lastCordovaResumeAt >= (pendingTopupSession.startedAt || 0)
    )
    const isTopupReturnContext = isTopupReturnContextByPath || hasCordovaResumeAfterPendingStart

    // В Cordova показываем "Деньги зачислятся..." только после фактического возврата
    // из банковского сценария, а не сразу после запуска внешней оплаты.
    if (isCordovaApp && !isTopupReturnContext) {
      setIsTopupBalancePolling(false)
      setActiveTopupSessionKey(null)
      return
    }

    const sessionStartedAt = pendingTopupSession?.startedAt || Date.now()
    const pollingStartedAt = Date.now()
    const activePendingSessionKey = pendingTopupSession?.sessionKey || `legacy_${sessionStartedAt}`
    const pendingTopupBaseline = pendingTopupSession?.baseline || null
    const deadlineAt = pollingStartedAt + TOPUP_BALANCE_POLL_MAX_DURATION_MS

    let isCancelled = false
    let timeoutId: number | null = null
    let wakeWaitUp: (() => void) | null = null
    let isNotificationCheckInProgress = false
    let isForcedSuccess = false
    const sessionId = topupBalancePollSessionRef.current + 1
    topupBalancePollSessionRef.current = sessionId
    setActiveTopupSessionKey(activePendingSessionKey)
    setIsTopupBalancePolling(true)

    const markForcedSuccess = (_source: string) => {
      if (isForcedSuccess) return
      isForcedSuccess = true
      if (wakeWaitUp) {
        wakeWaitUp()
      }
    }

    const wait = async (ms: number) => {
      if (ms <= 0) return

      await new Promise<void>((resolve) => {
        let isSettled = false
        const finalize = () => {
          if (isSettled) return
          isSettled = true
          if (timeoutId !== null) {
            window.clearTimeout(timeoutId)
            timeoutId = null
          }
          if (wakeWaitUp === finalize) {
            wakeWaitUp = null
          }
          resolve()
        }

        wakeWaitUp = finalize
        timeoutId = window.setTimeout(finalize, ms)
      })
    }

    const isSessionSuperseded = () => {
      const latestPendingSession = (
        readTopupBalancePendingSession()
        || topupPendingFallbackRef.current
      )
      if (!latestPendingSession) return false
      return latestPendingSession.sessionKey !== activePendingSessionKey
    }

    const isSessionRuntimeActive = () => (
      !isCancelled && topupBalancePollSessionRef.current === sessionId
    )

    const isCurrentSessionActive = () => (
      isSessionRuntimeActive() && !isSessionSuperseded()
    )

    const refetchBalanceQuery = async () => {
      await queryClient.invalidateQueries({ queryKey: [ rqKeys.userBalance ], exact: true })
      await queryClient.refetchQueries({ queryKey: [ rqKeys.userBalance ], exact: true, type: 'all' })
    }

    const refetchTransactionsInBackground = () => {
      if (!isOnline()) return
      void queryClient.invalidateQueries({ queryKey: [ rqKeys.transactionsKey ], exact: false })
      void queryClient.refetchQueries({ queryKey: [ rqKeys.transactionsKey ], exact: false, type: 'all' })
    }

    const refetchTransactionsOnce = async () => {
      if (!isOnline()) return
      await queryClient.invalidateQueries({ queryKey: [ rqKeys.transactionsKey ], exact: false })
      await queryClient.refetchQueries({ queryKey: [ rqKeys.transactionsKey ], exact: false, type: 'all' })
    }

    const pollBalanceAndDetectChange = async (
      initialSignatureValue: string | null
    ): Promise<{ changed: boolean, nextInitialSignature: string | null }> => {
      let nextInitialSignature = initialSignatureValue

      try {
        // Do not spend attempts while offline; next scheduled check will try again.
        if (!isOnline()) {
          return {
            changed: false,
            nextInitialSignature,
          }
        }

        await refetchBalanceQuery()
      } catch (e) {
        console.warn('[topup-return] balance poll failed', e)
        return {
          changed: false,
          nextInitialSignature,
        }
      }

      const nextSignature = getBalanceSignature(
        queryClient.getQueryData<T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }>([ rqKeys.userBalance ])
      )

      if (!nextSignature) {
        return {
          changed: false,
          nextInitialSignature,
        }
      }

      if (!nextInitialSignature) {
        nextInitialSignature = nextSignature
        return {
          changed: false,
          nextInitialSignature,
        }
      }

      const changed = nextSignature !== nextInitialSignature

      return {
        changed,
        nextInitialSignature,
      }
    }

    const detectImmediateSuccessFromBaseline = async () => {
      if (!pendingTopupBaseline) {
        return false
      }

      try {
        if (!isOnline()) {
          return false
        }

        await refetchBalanceQuery()
      } catch (e) {
        console.warn('[topup-return] baseline check failed', e)
        return false
      }

      const currentBalanceData = queryClient.getQueryData<T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }>([ rqKeys.userBalance ])
      const currentSnapshot = getBalanceAccountSnapshot(currentBalanceData, pendingTopupBaseline)
      if (!currentSnapshot) {
        return false
      }

      const amountChanged = currentSnapshot.amountValue !== pendingTopupBaseline.amountValue
      const blockedChanged = currentSnapshot.blockedValue !== pendingTopupBaseline.blockedValue
      const changed = amountChanged || blockedChanged

      return changed
    }

    const checkRealtimeBalanceSignal = async (
      source: string,
      _eventPayload?: unknown
    ) => {
      if (!pendingTopupBaseline || !isCurrentSessionActive()) {
        return
      }
      if (isNotificationCheckInProgress) {
        return
      }

      isNotificationCheckInProgress = true
      try {
        if (!isOnline()) {
          return
        }

        await refetchBalanceQuery()
        if (!isCurrentSessionActive()) return

        const currentBalanceData = queryClient.getQueryData<T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }>([ rqKeys.userBalance ])
        const currentSnapshot = getBalanceAccountSnapshot(currentBalanceData, pendingTopupBaseline)
        if (!currentSnapshot) {
          return
        }

        const changed = (
          currentSnapshot.amountValue !== pendingTopupBaseline.amountValue
          || currentSnapshot.blockedValue !== pendingTopupBaseline.blockedValue
        )

        if (changed) {
          markForcedSuccess(source)
        }
      } catch (e) {
        console.warn('[topup-return] realtime balance signal check failed', e)
      } finally {
        isNotificationCheckInProgress = false
      }
    }

    const runTopupBalancePolling = async () => {
      type T_TopupPollingResult = 'success' | 'timeout' | 'cancelled'

      let initialSignature: string | null = null

      const pollScheduleMs: number[] = [ ...TOPUP_BALANCE_POLL_ABSOLUTE_POINTS_MS ]
      for (
        let nextPointMs = TOPUP_BALANCE_POLL_REPEAT_START_MS;
        nextPointMs <= TOPUP_BALANCE_POLL_MAX_DURATION_MS;
        nextPointMs += TOPUP_BALANCE_POLL_REPEAT_DELAY_MS
      ) {
        pollScheduleMs.push(nextPointMs)
      }

      const baselineSuccess = await detectImmediateSuccessFromBaseline()
      if (!isCurrentSessionActive()) return 'cancelled' as T_TopupPollingResult
      if (baselineSuccess) {
        return 'success' as T_TopupPollingResult
      }
      if (isForcedSuccess) return 'success' as T_TopupPollingResult

      initialSignature = getBalanceSignature(
        queryClient.getQueryData<T_UniRes<T_UserBalanceRes> & { _requestRevision?: number }>([ rqKeys.userBalance ])
      )

      for (const pollPointMs of pollScheduleMs) {
        if (isForcedSuccess) return 'success' as T_TopupPollingResult

        const timeLeftMs = deadlineAt - Date.now()
        if (timeLeftMs <= 0) return 'timeout' as T_TopupPollingResult

        const pollAt = pollingStartedAt + pollPointMs
        if (pollAt <= Date.now()) {
          continue
        }

        const waitMs = pollAt - Date.now()
        if (waitMs > 0) {
          await wait(Math.min(waitMs, timeLeftMs))
        }

        if (isForcedSuccess) return 'success' as T_TopupPollingResult
        if (!isCurrentSessionActive()) return 'cancelled' as T_TopupPollingResult
        if (Date.now() >= deadlineAt) return 'timeout' as T_TopupPollingResult

        const {
          changed,
          nextInitialSignature,
        } = await pollBalanceAndDetectChange(initialSignature)

        initialSignature = nextInitialSignature

        if (!isCurrentSessionActive()) return 'cancelled' as T_TopupPollingResult
        if (changed) {
          return 'success' as T_TopupPollingResult
        }
      }

      return 'timeout' as T_TopupPollingResult
    }

    void (async () => {
      const transactionsSignatureBeforeSuccess = getTransactionsSignature(
        queryClient.getQueryData<T_UniRes<T_TransactionsListRes>>([ rqKeys.transactionsKey ])
      )

      const result = await runTopupBalancePolling()
      if (!isCurrentSessionActive()) return

      if (result === 'success') {
        let hasFreshTransactions = false
        try {
          await refetchTransactionsOnce()
          const transactionsSignatureAfterSingleRefetch = getTransactionsSignature(
            queryClient.getQueryData<T_UniRes<T_TransactionsListRes>>([ rqKeys.transactionsKey ])
          )
          hasFreshTransactions = (
            !!transactionsSignatureAfterSingleRefetch
            && transactionsSignatureAfterSingleRefetch !== transactionsSignatureBeforeSuccess
          )
        } catch {
          hasFreshTransactions = false
        }
        if (!isCurrentSessionActive()) return
        clearTopupBalancePendingSession()
        setDismissedTopupSessionKey(null)
        setActiveTopupSessionKey(null)
        topupPendingFallbackRef.current = null
        setIsTopupBalancePolling(false)
        navigate(routes.home, { replace: true })
        if (!hasFreshTransactions) {
          refetchTransactionsInBackground()
        }
        return
      }

      if (result === 'timeout') {
        clearTopupBalancePendingSession()
        setDismissedTopupSessionKey(null)
        setActiveTopupSessionKey(null)
        topupPendingFallbackRef.current = null
      }

      setIsTopupBalancePolling(false)
    })()

    const handlePendingSessionUpdated = (event: Event) => {
      const storageSession = readTopupBalancePendingSession()
      const customEvent = event as CustomEvent<{
        action?: 'set' | 'clear'
        session?: T_TopupBalancePendingSession
      }>

      if (storageSession) {
        topupPendingFallbackRef.current = storageSession
      } else if (customEvent?.detail?.action === 'set' && customEvent.detail.session) {
        topupPendingFallbackRef.current = customEvent.detail.session
      } else if (customEvent?.detail?.action === 'clear' || !hasTopupBalancePendingCookie()) {
        topupPendingFallbackRef.current = null
      }

      if (!isSessionRuntimeActive()) return
      if (!isSessionSuperseded()) return
      isCancelled = true
      topupBalancePollSessionRef.current += 1
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
        timeoutId = null
      }
      if (wakeWaitUp) {
        wakeWaitUp()
      }
      setIsTopupBalancePolling(false)
      setActiveTopupSessionKey(null)
    }

    const handleTopupBalanceChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{
        eventName?: string
        eventTime?: number
        payload?: unknown
      }>

      void checkRealtimeBalanceSignal(
        customEvent?.detail?.eventName || TOPUP_BALANCE_CHANGED_EVENT,
        customEvent?.detail?.payload
      )
    }

    window.addEventListener(TOPUP_BALANCE_PENDING_UPDATED_EVENT, handlePendingSessionUpdated)
    window.addEventListener(TOPUP_BALANCE_CHANGED_EVENT, handleTopupBalanceChanged)

    return () => {
      isCancelled = true
      if (timeoutId !== null) window.clearTimeout(timeoutId)
      if (wakeWaitUp) {
        wakeWaitUp()
      }
      window.removeEventListener(TOPUP_BALANCE_PENDING_UPDATED_EVENT, handlePendingSessionUpdated)
      window.removeEventListener(TOPUP_BALANCE_CHANGED_EVENT, handleTopupBalanceChanged)
    }
  }, [ isAuthorized, location.pathname, navigate, queryClient, topupPendingRevision, lastCordovaResumeAt ])


  // Скелетон до проверки статуса авторизованности
  if (!isAuthCheckedOnLoad) {
    return (
      <div style={{ padding: '20px' }}>
        <Skeleton count={5} title={false} />
      </div>
    )
  }

  const isTopupLoaderVisible = isTopupBalancePolling
    && (
      activeTopupSessionKey === null
      || activeTopupSessionKey !== dismissedTopupSessionKey
    )

  const handleCloseTopupLoader = () => {
    if (activeTopupSessionKey !== null) {
      setDismissedTopupSessionKey(activeTopupSessionKey)
    }
    navigate(routes.home, { replace: true })
  }


  return <>
    {isTopupLoaderVisible && (
      <TopupBalancePollingLoader onClose={handleCloseTopupLoader} />
    )}
    {children}
  </>
}
